import { useState, useReducer, Reducer, useCallback, useRef, useEffect } from 'react';

import { UseValidateHook } from '../../utils/validation/validateTypes';
import { Form } from './types';

const initialErrorState = {};

const useForm = <TState, TAction, TError extends object, TActionType = unknown>(
    validateHook: UseValidateHook<TState, TError>,
    reducer: Reducer<TState, TAction>,
    initialState: TState,
): Form<TState, TAction, TError, TActionType> => {
    const [state, dispatch] = useReducer<Reducer<TState, TAction>, TState>(reducer, initialState, () => initialState);
    const [errors, setErrors] = useState<TError>(initialErrorState as TError);
    const isMountedRef = useRef<boolean>(true);

    const [validateIsLoading, setValidateIsLoading] = useState(false);

    const validate = validateHook();

    const checkValidityAndSetErrors = useCallback(async () => {
        setValidateIsLoading(true);
        const validationErrors = await validate(state);
        setValidateIsLoading(false);
        setErrors(validationErrors);
        return !Object.entries(validationErrors).some(([, value]) => value);
    }, [validate, state]);

    const handleChange = useCallback((action: TAction): void => {
        if (!isMountedRef.current) {
            // don't update component if it has been unmounted
            return;
        }
        dispatch(action);
        setErrors(initialErrorState as TError);
    }, []);

    const curryChange = <TPayload = unknown>(type: TActionType) => {
        return (payload: TPayload) => {
            handleChange({ type, payload } as unknown as TAction);
        };
    };

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    return {
        validateIsLoading,
        checkFormValidity: checkValidityAndSetErrors,
        handleChange,
        state,
        errors,
        curryChange,
    };
};

export default useForm;
